import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { hasConsent } from './ResultCard';

/*
 * Click-to-play YouTube facade (dark-section treatment, mirrors the landing
 * testimonial tiles). The initial DOM contains NO iframe — just the poster (or
 * striped placeholder) and a play button; the YouTube embed is injected only on
 * click. Videos are YouTube-unlisted; only the ID is stored in data. Posters
 * are lazy-loaded.
 *
 * CONSENT GATE — enforced here as well as in the page: no consent, no render.
 */

const STRIPES_DARK =
  'repeating-linear-gradient(135deg, rgba(255,255,255,.07) 0 8px, rgba(255,255,255,.12) 8px 16px)';

const ResultVideoTile = ({ entry }) => {
  const [playing, setPlaying] = useState(false);
  if (!hasConsent(entry)) return null;
  const { studentName, batchYear, youtubeId, posterSrc, caption } = entry;
  const metaLine = [caption, batchYear].filter(Boolean).join(' · ');

  return (
    <div className="bg-white/[0.06] border border-white/[0.14] rounded-[13px] lg:rounded-[14px] overflow-hidden flex flex-col">
      <div className="relative aspect-[9/16]">
        {playing ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1`}
            title={`${studentName} — result story`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${studentName}'s result story`}
            className="absolute inset-0 w-full h-full flex items-center justify-center"
            style={posterSrc ? undefined : { background: STRIPES_DARK }}
          >
            {posterSrc && (
              <img
                src={posterSrc}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            {!posterSrc && (
              <span className="absolute bottom-[7px] left-[9px] font-mono text-[9px] lg:text-[9.5px] text-white/45">
                placeholder · video poster 9:16
              </span>
            )}
            <span className="relative w-12 h-12 lg:w-[54px] lg:h-[54px] rounded-full bg-brand-amber text-brand-deep flex items-center justify-center">
              <Play className="w-4 h-4 lg:w-5 lg:h-5 ml-0.5" fill="currentColor" />
            </span>
          </button>
        )}
      </div>
      <div className="px-[13px] pt-[13px] pb-[15px] lg:px-4 lg:pt-4 lg:pb-[18px] flex flex-col gap-[3px] lg:gap-1">
        <span className="text-[13.5px] lg:text-[15px] font-bold leading-tight text-white">
          {studentName}
        </span>
        {metaLine && (
          <span className="text-[11.5px] lg:text-[12.5px] leading-snug text-white/60">
            {metaLine}
          </span>
        )}
      </div>
    </div>
  );
};

export default ResultVideoTile;
